import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Task } from '../types';
import { fetchGlobalTasks, fetchGlobalTaskCounts, GlobalTaskCounts, updateTask as apiUpdateTask } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { encodeId } from '../utils/hashids';
import AddTaskModal from '../components/Tasks/AddTaskModal';

// Map polymorphic taskable_type ('App\Models\Lead' etc.) → API resource path.
const taskableResource = (type: string): 'leads' | 'deals' | 'customers' | 'contacts' | null => {
  if (type.includes('Lead'))     return 'leads';
  if (type.includes('Deal'))     return 'deals';
  if (type.includes('Customer')) return 'customers';
  if (type.includes('Contact'))  return 'contacts';
  return null;
};

const PRIORITY_LABEL: Record<string, string> = { low: 'LOW', medium: 'MEDIUM', high: 'HIGH' };
const PRIORITY_COLOR: Record<string, string> = {
  low: '#888', medium: 'var(--amber)', high: 'var(--coral)',
};

// Local-timezone YYYY-MM-DD. Previously used toISOString() which returns
// UTC, flipping task buckets wrong for IST users from midnight to 05:30.
function isoToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function isOverdue(t: Task) {
  if (!t.due_date || t.is_done) return false;
  return t.due_date < isoToday();
}
function isToday(t: Task) {
  return !t.is_done && t.due_date === isoToday();
}
function isUpcoming(t: Task) {
  if (!t.due_date || t.is_done) return false;
  return t.due_date > isoToday();
}
function isCompletedToday(t: Task) {
  if (!t.is_done || !t.updated_at) return false;
  // updated_at is an ISO timestamp; comparing its LOCAL date to isoToday()
  // keeps the "today" definition consistent with the other bucket checks.
  const d = new Date(t.updated_at);
  const local = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  return local === isoToday();
}

function entityLabel(t: Task) {
  if (!t.taskable_type) return '';
  if (t.taskable_type.includes('Lead')) return 'Lead';
  if (t.taskable_type.includes('Deal')) return 'Deal';
  return '';
}

function timeLabel(t: Task) {
  if (!t.due_date) return '';
  const d = new Date(t.due_date);
  const label = d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  return t.due_time ? `${label} ${t.due_time}` : label;
}

// Mini calendar
function buildCalendar(year: number, month: number, taskDates: Set<string>) {
  const first = new Date(year, month, 1).getDay();
  const days  = new Date(year, month + 1, 0).getDate();
  return { first, days };
}

const MONTH_NAMES = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];
const DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const TasksPage: React.FC = () => {
  const { user } = useAuth();
  const canSeeAllScope = user
    ? ['super_admin', 'tenant_admin', 'manager', 'marketing'].includes(user.role)
    : false;
  // Super_admin has no personal tasks and always wants tenant-wide by default.
  // Admins/managers default to 'mine' so their inbox stays personal-first.
  const [scope, setScope] = useState<'mine' | 'all'>(
    user?.role === 'super_admin' ? 'all' : 'mine'
  );

  const [tasks, setTasks]             = useState<Task[]>([]);
  const [counts, setCounts]           = useState<GlobalTaskCounts | null>(null);
  const [loading, setLoading]         = useState(true);
  const [openSections, setOpen]       = useState<Record<string, boolean>>({
    // Today leads — it's what the rep acts on NOW. Overdue second so
    // misses are still visible without pushing today's work down. Keep
    // Completed open so a just-checked task doesn't appear to "vanish".
    today: true, overdue: true, upcoming: false, completed: true, date: true,
  });
  const now         = new Date();
  const [calYear, setCalYear]     = useState(now.getFullYear());
  const [calMonth, setCalMonth]   = useState(now.getMonth());   // 0..11
  // Calendar-driven filter: clicking a day cell narrows the list to that date.
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  // Add Task modal — standalone modal with Lead/Deal picker so reps can
  // create tasks straight from this page instead of navigating to a
  // detail view first.
  const [addOpen, setAddOpen] = useState(false);

  const goPrevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const goNextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };
  const goToday = () => {
    setCalYear(now.getFullYear());
    setCalMonth(now.getMonth());
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Tasks list is capped at 500 server-side; counts come from a
      // separate DB-count endpoint so the sidebar stays accurate even
      // when the list is truncated.
      const [list, c] = await Promise.all([
        fetchGlobalTasks({ scope }),
        fetchGlobalTaskCounts({ scope }).catch(() => null),
      ]);
      setTasks(list);
      setCounts(c);
    } catch {
      setTasks([]);
      setCounts(null);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  // Optimistic checkbox toggle — mutate one task's is_done in local state
  // so the UI reflects the click instantly, even if the refetch is slow
  // or fails. Called from TaskRow's handleToggle.
  const applyOptimisticDone = useCallback((taskId: number, isDone: boolean) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, is_done: isDone } : t));
  }, []);

  const overdue       = tasks.filter(isOverdue);
  const todayArr      = tasks.filter(isToday);
  const upcoming      = tasks.filter(isUpcoming);
  const completedList = tasks.filter(isCompletedToday);
  const forSelectedDate = selectedDate
    ? tasks.filter(t => t.due_date === selectedDate)
    : [];

  const taskDates = new Set(tasks.filter(t => t.due_date).map(t => t.due_date!));
  const { first, days } = buildCalendar(calYear, calMonth, taskDates);
  // "Today" indicator only when the calendar is showing the current month.
  const isCurrentMonth = calYear === now.getFullYear() && calMonth === now.getMonth();
  const todayDate = isCurrentMonth ? now.getDate() : -1;

  const toggle = (key: string) => setOpen(o => ({ ...o, [key]: !o[key] }));

  // Empty-state copy is bucket-specific — a flat "No tasks here" tells
  // the rep nothing; "You're clear for today" vs "No overdue tasks"
  // vs "Nothing scheduled" each confirms a different thing worth
  // confirming.
  const EMPTY_COPY: Record<string, string> = {
    today:     'All clear for today — nothing on your plate.',
    overdue:   'No overdue tasks. Nice work staying on top.',
    upcoming:  'No upcoming tasks scheduled.',
    completed: 'Nothing finished yet today. Check items off as you go.',
    date:      'No tasks on this day.',
  };

  // NOTE: Section is inline, but items/toggle/openSections come from
  // closure. To avoid the re-mount-every-render problem (new component
  // reference each render kills TaskRow state), use a plain render
  // helper (not a component) — React treats this as normal JSX rather
  // than a component boundary.
  const renderSection = (skey: string, label: string, count: number, dot: string, items: Task[]) => (
    <div className="tasks-section" key={skey}>
      <button className="tasks-section-header" onClick={() => toggle(skey)}>
        <span className="tasks-section-dot" style={dot ? { background: dot } : {}} />
        <span className="tasks-section-label">{label} ({count})</span>
        <span className="tasks-section-badge">{count}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ marginLeft: 'auto', transform: openSections[skey] ? 'rotate(180deg)' : undefined, transition: 'transform .2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {openSections[skey] && (
        <div className="tasks-section-body">
          {items.length === 0
            ? <div className="tasks-empty">{EMPTY_COPY[skey] ?? 'No tasks here'}</div>
            : items.map(t => <TaskRow key={t.id} task={t} onRefresh={load} onOptimistic={applyOptimisticDone} />)}
        </div>
      )}
    </div>
  );

  return (
    <div className="tasks-page">
      <div className="tasks-layout">
        {/* Left: task list */}
        <div className="tasks-main">
          <div className="page-hd" style={{ marginBottom: 20 }}>
            <div className="page-hd-left">
              <h1 className="page-title">
                {scope === 'all' ? 'All Tasks' : 'My Tasks'}
              </h1>
              <p className="page-meta">
                {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                {scope === 'all' && <> · <strong>Tenant-wide view</strong></>}
              </p>
            </div>
            <div className="page-hd-right" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {canSeeAllScope && (
                <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <button
                    onClick={() => setScope('mine')}
                    style={{
                      padding: '6px 14px', fontSize: 13, fontWeight: 600,
                      border: 'none', cursor: 'pointer',
                      background: scope === 'mine' ? 'var(--primary)' : '#fff',
                      color:      scope === 'mine' ? '#fff'           : 'var(--text-2)',
                    }}
                  >My Tasks</button>
                  <button
                    onClick={() => setScope('all')}
                    style={{
                      padding: '6px 14px', fontSize: 13, fontWeight: 600,
                      border: 'none', cursor: 'pointer',
                      background: scope === 'all' ? 'var(--primary)' : '#fff',
                      color:      scope === 'all' ? '#fff'           : 'var(--text-2)',
                    }}
                  >All Tasks</button>
                </div>
              )}
              <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
                + Add Task
              </button>
            </div>
          </div>

          {loading ? <div className="spinner">Loading…</div> : selectedDate ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Filtered to <strong style={{ color: 'var(--text)' }}>{new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</strong> · {forSelectedDate.length} task{forSelectedDate.length === 1 ? '' : 's'}
                </span>
                <button
                  onClick={() => setSelectedDate(null)}
                  style={{ padding: '4px 10px', fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}
                >
                  Clear filter
                </button>
              </div>
              {renderSection('date', 'Tasks on this date', forSelectedDate.length, 'var(--primary)', forSelectedDate)}
            </>
          ) : (
            <>
              {/* Order matches rep workflow: what to do NOW (today), what
                  slipped (overdue), what's coming (upcoming), what's done.
                  Section counts show the LIST-level count (list is capped
                  at 500 server-side); the sidebar Summary uses DB counts.
                  The numbers can legitimately differ on large tenants. */}
              {renderSection('today', 'Today', todayArr.length, 'var(--amber)', todayArr)}
              {overdue.length > 0 && renderSection('overdue', 'Overdue', overdue.length, 'var(--coral)', overdue)}
              {renderSection('upcoming', 'Upcoming', upcoming.length, 'var(--primary)', upcoming)}
              {renderSection('completed', 'Completed Today', completedList.length, 'var(--teal)', completedList)}
            </>
          )}
        </div>

        {/* Right: calendar + summary */}
        <div className="tasks-sidebar">
          {/* Calendar */}
          <div className="tasks-calendar-card">
            <div className="tasks-cal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <button
                type="button"
                onClick={goPrevMonth}
                title="Previous month"
                style={{
                  background: 'transparent', border: '1px solid var(--border)',
                  borderRadius: 6, padding: '2px 8px', cursor: 'pointer',
                  fontSize: 14, lineHeight: 1, color: 'var(--text-2)',
                }}
              >‹</button>
              <button
                type="button"
                onClick={goToday}
                title="Jump to today"
                style={{
                  background: 'transparent', border: 'none', padding: 0,
                  fontSize: 14, fontWeight: 600, color: 'var(--navy)', cursor: 'pointer',
                }}
              >
                {MONTH_NAMES[calMonth]} {calYear}
              </button>
              <button
                type="button"
                onClick={goNextMonth}
                title="Next month"
                style={{
                  background: 'transparent', border: '1px solid var(--border)',
                  borderRadius: 6, padding: '2px 8px', cursor: 'pointer',
                  fontSize: 14, lineHeight: 1, color: 'var(--text-2)',
                }}
              >›</button>
            </div>
            <div className="tasks-cal-grid">
              {DAY_NAMES.map(d => <div key={d} className="tasks-cal-dow">{d}</div>)}
              {Array.from({ length: first }).map((_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: days }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const hasTask = taskDates.has(dateStr);
                const isCurrentDay = day === todayDate;
                const isSelected = selectedDate === dateStr;

                // Cell background — precedence: selected > today > hover-ready.
                // White text ONLY when the cell has a colored background so we
                // never render white-on-white (regression from prior version
                // that stripped the .tasks-cal-today purple background but
                // kept its white color).
                const hasSolidBg = isSelected || isCurrentDay;
                const bg = isSelected ? 'var(--primary)'
                         : isCurrentDay ? 'var(--primary)'
                         : 'transparent';

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setSelectedDate(prev => prev === dateStr ? null : dateStr)}
                    title={hasTask ? `Show tasks on ${dateStr}` : 'No tasks on this day'}
                    style={{
                      cursor: 'pointer',
                      background: bg,
                      color: hasSolidBg ? '#fff' : 'var(--text-2)',
                      border: '1px solid transparent',
                      borderRadius: hasSolidBg ? '50%' : 4,
                      width: hasSolidBg ? 26 : undefined,
                      height: hasSolidBg ? 26 : undefined,
                      padding: hasSolidBg ? 0 : '4px 2px',
                      font: 'inherit',
                      fontWeight: isCurrentDay ? 700 : 400,
                      textAlign: 'center',
                      position: 'relative',
                      margin: hasSolidBg ? 'auto' : undefined,
                      display: hasSolidBg ? 'flex' : undefined,
                      alignItems: hasSolidBg ? 'center' : undefined,
                      justifyContent: hasSolidBg ? 'center' : undefined,
                    }}
                  >
                    {day}
                    {hasTask && (
                      <span style={{
                        position: 'absolute', bottom: 1, left: '50%',
                        transform: 'translateX(-50%)',
                        width: 4, height: 4, borderRadius: '50%',
                        background: hasSolidBg ? 'rgba(255,255,255,.85)' : 'var(--primary)',
                        display: 'block',
                      }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Today's Summary — sourced from /tasks/counts (DB-side) so
              the numbers stay accurate when the list is server-truncated.
              Falls back to list-derived counts if the counts call failed. */}
          {(() => {
            const overdueCount        = counts?.overdue        ?? overdue.length;
            const todayOpenCount      = counts?.today          ?? todayArr.length;
            const completedTodayCount = counts?.completed_today ?? completedList.length;
            const todayTotal          = todayOpenCount + completedTodayCount;
            const pct                 = todayTotal > 0 ? Math.round(completedTodayCount / todayTotal * 100) : 0;
            return (
              <div className="tasks-summary-card">
                <h4 className="tasks-summary-title">Today's Summary</h4>
                <div className="tasks-summary-row">
                  <span>Tasks Today</span>
                  <strong>{todayTotal}</strong>
                </div>
                <div className="tasks-summary-row">
                  <span>Overdue</span>
                  <strong style={{ color: 'var(--coral)' }}>{overdueCount}</strong>
                </div>
                <div className="tasks-summary-row">
                  <span>Completed</span>
                  <strong style={{ color: 'var(--teal)' }}>{completedTodayCount}</strong>
                </div>
                <div className="tasks-progress-bar">
                  <div className="tasks-progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="tasks-summary-sub">
                  {completedTodayCount}/{todayTotal} completed
                </div>
              </div>
            );
          })()}

        </div>
      </div>

      <AddTaskModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={load}
      />
    </div>
  );
};

const TaskRow: React.FC<{ task: Task; onRefresh: () => void; onOptimistic: (id: number, isDone: boolean) => void }> = ({ task, onRefresh, onOptimistic }) => {
  const history = useHistory();
  const overdue = isOverdue(task);
  const ent = entityLabel(task);
  const isLead = ent === 'Lead';
  const isDeal = ent === 'Deal';
  const name  = task.taskable?.name ?? task.taskable?.title ?? null;
  const phone = task.taskable?.mobile || task.taskable?.phone || null;
  const [toggling, setToggling] = useState(false);

  // Toggle done state via the polymorphic tasks endpoint. Route depends on
  // taskable_type — Lead-tasks go to /leads/{id}/tasks/{taskId} etc.
  // Optimistic update: flip the parent state IMMEDIATELY so the checkbox
  // visual is unmistakable, then reconcile with the server.
  const handleToggle = async () => {
    const resource = taskableResource(task.taskable_type);
    if (!resource || toggling) return;
    const next = !task.is_done;
    setToggling(true);
    onOptimistic(task.id, next);   // instant UI flip
    try {
      await apiUpdateTask(resource, task.taskable_id, task.id, { is_done: next });
      onRefresh();                 // reconcile with server truth
    } catch (e) {
      onOptimistic(task.id, !next); // revert on failure
      // eslint-disable-next-line no-alert
      alert('Could not update task. Try again.');
    } finally {
      setToggling(false);
    }
  };

  // Land on the Timeline tab so the rep sees the conversation context.
  const openLeadTimeline = () => {
    if (!isLead) return;
    history.push(`/leads/${encodeId(task.taskable_id)}?tab=timeline`);
  };

  return (
    <div className={`task-row${task.is_done ? ' task-row-done' : ''}${overdue ? ' task-row-overdue' : ''}`}>
      <input
        type="checkbox"
        className="task-checkbox"
        checked={task.is_done}
        disabled={toggling || !taskableResource(task.taskable_type)}
        onChange={handleToggle}
      />
      <div className="task-row-body">
        <div className="task-row-title">{task.title}</div>
        {ent && (
          <div className="task-row-meta">
            <span className="task-entity-tag">{ent}:</span>
            <span className="task-entity-name">
              {name ?? `#${task.taskable_id}`}
            </span>
            {isLead && phone && (
              <button
                type="button"
                onClick={openLeadTimeline}
                title="Open lead timeline"
                className="task-entity-phone"
                style={{
                  marginLeft: 6, fontSize: 12, color: 'var(--primary)',
                  background: 'none', border: 'none', padding: 0,
                  cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                · {phone}
              </button>
            )}
            {isDeal && task.taskable && (
              <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                #{task.taskable_id}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="task-row-right">
        <span className="task-row-time" style={{ color: overdue ? 'var(--coral)' : 'var(--text-muted)' }}>
          {timeLabel(task)}
        </span>
        {task.priority && (
          <span className="task-priority-badge" style={{ color: PRIORITY_COLOR[task.priority] ?? '#888' }}>
            {PRIORITY_LABEL[task.priority]}
          </span>
        )}
        {task.is_auto && <span className="task-auto-badge">Auto</span>}
      </div>
    </div>
  );
};

export default TasksPage;
